// src/components/ModelMasterPanel.js
//
// 🎬 建模大师面板 —— 画布旁分屏的对话式工具箱
//
// 结构：
//   ┌──────────────┬────────────────────┐
//   │ 对话历史侧栏  │  消息列表（流式）    │
//   │ + 新建对话    │  ───────────────   │
//   │ 对话1/对话2…  │  输入框 + 模型 + 🎤  │
//   └──────────────┴────────────────────┘
//
// 流式：组件自建 Socket.IO 连接（服务端按 token 自动 join private-userId 房间），
// 监听 model-master-token / done / error，把增量逐字追加到当前 assistant 气泡。
//
// 对话记录：按用户存（GET/POST/PATCH/DELETE /api/ai/model-master/conversations）。
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { io } from 'socket.io-client';
import { Plus, Send, Trash2, Edit2, MessageSquare, Loader2, ChevronDown, Bot, Sparkle, ScrollText, PanelLeftClose, PanelBottomClose, Copy, FilePlus, Clapperboard, X, Image as ImageIcon } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import api from '../api';
import * as C from '../constants';
import VoiceMicButton from './VoiceMicButton';
import { copyTextToClipboard } from '../utils/browserCompat'; // http 站点没有 navigator.clipboard

// ── Markdown 渲染：贴合暗色面板的元素样式 ──
// react-markdown 默认样式很挤，这里逐元素定制间距/层级/配色，把模型吐出的
// 标题/列表/加粗/引用/代码块/表格排成规整段落。
// 🆕 @资产名 高亮：在渲染前预处理文本，把 @「资产名」或 @资产名 替换成 **@「资产名」**
// 这样 Markdown 的 strong 规则会自动标黄
const MENTION_RE = /@(?:「[^」]+」|[一-龥a-zA-Z0-9_]+)/g;
function preprocessMentions(text) {
  if (!text || typeof text !== 'string') return text;

  // 先移除已有的 ** 标记避免重复包裹
  const placeholders = [];
  let idx = 0;

  // 先提取已经被 ** 包裹的 @提及
  text = text.replace(/\*\*(@(?:「[^」]+」|[^\s@*]+))\*\*/g, (match, mention) => {
    const placeholder = `__MENTION_${idx}__`;
    placeholders.push({ placeholder, mention });
    idx++;
    return placeholder;
  });

  // 然后给剩余的 @提及 加上 ** 包裹（两侧加零宽空格防止 Markdown 把标点合并进来）
  text = text.replace(MENTION_RE, match => `​**${match}**​`);

  // 恢复已有的 ** 包裹的 @提及
  for (const { placeholder, mention } of placeholders) {
    text = text.replace(placeholder, `​**${mention}**​`);
  }

  return text;
}

const mdComponents = {
  p:  ({ node, ...p }) => <p style={{ margin: '0 0 10px', lineHeight: 1.7 }} {...p} />,
  h1: ({ node, ...p }) => <h1 style={{ fontSize: 18, fontWeight: 700, margin: '14px 0 8px', color: '#f8fafc' }} {...p} />,
  h2: ({ node, ...p }) => <h2 style={{ fontSize: 16, fontWeight: 700, margin: '14px 0 8px', color: '#f8fafc' }} {...p} />,
  h3: ({ node, ...p }) => <h3 style={{ fontSize: 15, fontWeight: 700, margin: '12px 0 6px', color: '#f1f5f9' }} {...p} />,
  ul: ({ node, ...p }) => <ul style={{ margin: '0 0 10px', paddingLeft: 22, lineHeight: 1.7 }} {...p} />,
  ol: ({ node, ...p }) => <ol style={{ margin: '0 0 10px', paddingLeft: 22, lineHeight: 1.7 }} {...p} />,
  li: ({ node, ...p }) => <li style={{ margin: '2px 0' }} {...p} />,
  strong: ({ node, ...p }) => <strong style={{ fontWeight: 700, color: '#fbbf24' }} {...p} />,
  em: ({ node, ...p }) => <em style={{ fontStyle: 'italic', color: '#cbd5e1' }} {...p} />,
  a:  ({ node, ...p }) => <a style={{ color: '#60a5fa', textDecoration: 'underline' }} target="_blank" rel="noreferrer" {...p} />,
  blockquote: ({ node, ...p }) => <blockquote style={{ margin: '0 0 10px', padding: '4px 12px', borderLeft: '3px solid #3b82f6', background: 'rgba(59,130,246,0.08)', color: '#cbd5e1', borderRadius: 4 }} {...p} />,
  hr: ({ node, ...p }) => <hr style={{ border: 'none', borderTop: `1px solid rgba(255,255,255,0.1)`, margin: '12px 0' }} {...p} />,
  code: ({ node, inline, ...p }) => inline
    ? <code style={{ background: 'rgba(255,255,255,0.1)', padding: '1px 5px', borderRadius: 4, fontSize: 13, fontFamily: 'monospace' }} {...p} />
    : <code style={{ display: 'block', background: 'rgba(0,0,0,0.35)', padding: 12, borderRadius: 8, fontSize: 13, fontFamily: 'monospace', overflowX: 'auto', lineHeight: 1.6 }} {...p} />,
  pre: ({ node, ...p }) => <pre style={{ margin: '0 0 10px' }} {...p} />,
  table: ({ node, ...p }) => <div style={{ overflowX: 'auto', margin: '0 0 10px' }}><table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 13 }} {...p} /></div>,
  th: ({ node, ...p }) => <th style={{ border: '1px solid rgba(255,255,255,0.12)', padding: '6px 10px', textAlign: 'left', background: 'rgba(255,255,255,0.06)', fontWeight: 700 }} {...p} />,
  td: ({ node, ...p }) => <td style={{ border: '1px solid rgba(255,255,255,0.12)', padding: '6px 10px' }} {...p} />,
};

// ── 选项块解析 ──
// 把正文按 [[CHOICES]] / [[CHOICES 问题标签]] ... [[/CHOICES]] 切成片段：
//   { type:'text', value }
//   { type:'choices', label, options:[...], blockIdx }   blockIdx 为该消息内选项块的序号
// 一条 assistant 消息里的所有选项块算作「一轮问询」：各选一个，凑齐后一次性提交。
// 流式中途若只出现了开标记还没闭合，则把开标记之后的内容暂时藏起来（不渲染半截按钮），
// 等 [[/CHOICES]] 到了或流结束再成型。
const CHOICE_CLOSE = '[[/CHOICES]]';
const CHOICE_OPEN_RE = /\[\[CHOICES(?:\s+([^\]]*))?\]\]/;
function parseChoiceSegments(content) {
  const segments = [];
  let rest = content || '';
  let blockIdx = 0;
  while (true) {
    const match = CHOICE_OPEN_RE.exec(rest);
    if (!match) {
      if (rest) segments.push({ type: 'text', value: rest });
      break;
    }
    const openIdx = match.index;
    const label = (match[1] || '').trim();
    // 开标记前的正文
    const before = rest.slice(0, openIdx);
    if (before) segments.push({ type: 'text', value: before });
    const afterOpen = rest.slice(openIdx + match[0].length);
    const closeIdx = afterOpen.indexOf(CHOICE_CLOSE);
    if (closeIdx === -1) {
      // 闭标记还没来（流式中途）：藏住未闭合部分，不渲染半截
      break;
    }
    const inner = afterOpen.slice(0, closeIdx);
    const options = inner.split('\n')
      .map(s => s.replace(/^[-*]\s*/, '').trim())  // 容忍模型给选项加 - / * 前缀
      .filter(Boolean);
    if (options.length) segments.push({ type: 'choices', label, options, blockIdx: blockIdx++ });
    rest = afterOpen.slice(closeIdx + CHOICE_CLOSE.length);
  }
  return segments;
}

// ── 剧本大纲「导入到分镜大师」──
// 建模大师最后输出的大纲，头行自带镜头数，如
//   「第一叙事段落: 梦想成真 & 出发（4镜/14秒）」
// 按「括号里含镜头数的头行」把大纲切成段落，读出该段镜头数 → 匹配 ≥ 该数的最小宫格档位。
// 宫格只有 4/9/16/25 四档（2×2/3×3/4×4/5×5），故映射粒度即这四档。
// 要求镜头数必须出现在括号内（如「（4镜…）」），避免正文里的「4镜头」等被误判为段落头。
const SHOT_COUNT_RE = /[（(][^）)]*?(\d+|[零一二三四五六七八九十两]+)\s*镜[^）)]*?[）)]/;

function cnNumToInt(str) {
  if (/^\d+$/.test(str)) return parseInt(str, 10);
  const map = { 零:0, 一:1, 二:2, 三:3, 四:4, 五:5, 六:6, 七:7, 八:8, 九:9, 十:10, 两:2 };
  let n = 0, tmp = 0;
  for (const ch of str) {
    const v = map[ch];
    if (v == null) continue;
    if (v === 10) { n += (tmp || 1) * 10; tmp = 0; }
    else tmp = v;
  }
  return n + tmp;
}

// 镜头数 → 宫格档位：取 ≥ 镜头数的最小档（4/9/16/25）
function shotCountToGrid(n) {
  if (n <= 4) return 4;
  if (n <= 9) return 9;
  if (n <= 16) return 16;
  return 25;
}

// 把一段正文按「含镜头数的头行」切成段落。
// 头行开启新段落，其后正文归入该段；首个头行之前的引言聚成无镜头数的段（不挂按钮）。
// 无任何头行时返回整段一条（shotCount 为 null），渲染与原来无差别。
function splitOutlineSegments(text) {
  const lines = (text || '').split(/\r?\n/);
  const segs = [];
  let cur = { lines: [], shotCount: null };
  for (const line of lines) {
    const m = line.match(SHOT_COUNT_RE);
    if (m) {
      if (cur.lines.length) segs.push(cur);
      const n = cnNumToInt(m[1]);
      cur = { lines: [line], shotCount: n > 0 ? n : null };
    } else {
      cur.lines.push(line);
    }
  }
  if (cur.lines.length) segs.push(cur);
  return segs.map(s => {
    let lines = s.lines.slice();
    // 去掉尾部空行
    while (lines.length && !lines[lines.length - 1].trim()) lines.pop();
    // 末行若是 markdown 分割线（--- / *** / ___），剥离出来单独渲染，
    // 好让「导入」按钮落在分割线上方（否则按钮显示在线下，像是下一段的按钮）
    let trailingRule = false;
    if (lines.length && /^\s*([-*_])(\s*\1){2,}\s*$/.test(lines[lines.length - 1])) {
      lines.pop();
      trailingRule = true;
      while (lines.length && !lines[lines.length - 1].trim()) lines.pop();
    }
    return { text: lines.join('\n').trim(), shotCount: s.shotCount, trailingRule };
  });
}

export default function ModelMasterPanel({ onClose, layout = 'left', onSpawnTextNode, onImportToStoryboard }) {
  const [conversations, setConversations] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);      // [{id, role, content}]
  const [models, setModels] = useState([]);
  const [modelId, setModelId] = useState(null);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);  // 对话目录悬浮窗：默认收起
  const [modelPickerOpen, setModelPickerOpen] = useState(false);  // 左下角模型下拉
  // 画布内弹窗：重命名 / 删除确认（替代系统 prompt/confirm）
  const [renameModal, setRenameModal] = useState(null);  // { id, value }
  const [deleteModal, setDeleteModal] = useState(null);  // { id }
  // 选中文本右键菜单：{ x, y, text }
  const [ctxMenu, setCtxMenu] = useState(null);
  // 大纲段落「导入到分镜大师」按钮 hover 高亮的 key（平时淡显，hover 才凸显）
  const [hoverImportKey, setHoverImportKey] = useState(null);
  // 选项草稿选择：messageId -> { [blockIdx]: 选中的选项文字 }
  // 一条 assistant 消息内的多组选择题各选一个，凑齐后一次性提交，不逐个发送。
  const [choiceDraft, setChoiceDraft] = useState({});
  // 输入区高度（仿模型节点控制台：可拖动上沿分割线调整）；持久化到 localStorage
  const [inputHeight, setInputHeight] = useState(() => {
    const saved = parseInt(localStorage.getItem('scriptMasterInputHeight'), 10);
    return Number.isFinite(saved) && saved >= 140 ? saved : 150;  // 兜底老数据，不低于下限 140
  });
  // 图片上传相关状态
  const [uploadedImages, setUploadedImages] = useState([]);  // base64 数组
  const fileInputRef = useRef(null);
  // 渲染状态提示
  const [renderingStatus, setRenderingStatus] = useState(null);  // "正在渲染中..."

  const socketRef = useRef(null);
  const activeIdRef = useRef(null);
  const streamingMsgIdRef = useRef(null);  // 当前正在追加的 assistant 消息 id（服务端真实 id）
  const scrollRef = useRef(null);
  const textareaRef = useRef(null);
  const modelBtnRef = useRef(null);   // 模型选择按钮，用于定位上拉菜单（portal 固定定位，避开祖先 overflow:hidden 裁切）
  const [modelMenuRect, setModelMenuRect] = useState(null);  // { left, bottom } 按钮在视口中的位置

  activeIdRef.current = activeId;

  // 记住上次激活的对话 id：切换/新建/发送即建时都会更新 activeId，统一在此持久化，
  // 下次打开面板据此恢复上次停留的对话（见初始化）。
  useEffect(() => {
    if (activeId) localStorage.setItem('scriptMasterLastConvId', String(activeId));
  }, [activeId]);

  // 当前选中模型的输出单价（积分/千 token），用于流式时实时预估消耗
  const currentModel = models.find(m => m.id === modelId);
  const pricePer1kOutput = currentModel?.pricePer1kOutput || 0;

  // 当前对话名（剧本名）：显示在顶栏"建模大师"之后，用《》包围
  const activeTitle = conversations.find(c => c.id === activeId)?.title || '';

  // 模型显示名：去掉"建模大师（）"外壳，只留括号内的真实模型名（如 DeepSeek）
  const cleanModelName = useCallback((name) => {
    if (!name) return '模型';
    const m = name.match(/[（(]([^）)]+)[）)]/);
    return m ? m[1].trim() : name;
  }, []);

  // 粗略 token 预估：DeepSeek 中文约 0.6 token/字，英文约 0.25 token/字。
  // 这里只用于流式中给用户一个"大概在涨"的体感，最终以服务端真实结算为准。
  const estimateTokens = useCallback((text) => {
    if (!text) return 0;
    const cjk = (text.match(/[一-鿿　-〿＀-￯]/g) || []).length;
    const other = text.length - cjk;
    return Math.ceil(cjk * 0.6 + other * 0.25);
  }, []);

  // ── 滚动到底部 ──
  // autoScrollRef：用户一旦用滚轮/拖动滚动条向上离开底部，就停掉自动跟随；
  // 滚回贴近底部再恢复。内容输出始终不间断（只控制视图是否跟随）。
  const autoScrollRef = useRef(true);
  const scrollToBottom = useCallback(() => {
    if (!autoScrollRef.current) return;
    requestAnimationFrame(() => {
      if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    });
  }, []);

  // 监听容器滚动：判断是否贴近底部（阈值 40px），据此开关自动跟随
  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
    autoScrollRef.current = nearBottom;
  }, []);

  // ── 初始化：拉模型 + 对话列表 ──
  useEffect(() => {
    (async () => {
      try {
        const [m, c] = await Promise.all([
          api.get('/api/ai/model-master/models'),
          api.get('/api/ai/model-master/conversations'),
        ]);
        const modelList = m?.models || [];
        setModels(modelList);
        if (modelList.length) setModelId(modelList[0].id);
        const convList = c?.conversations || [];
        setConversations(convList);
        if (convList.length) {
          // 记忆上次对话：优先恢复上次激活的那个（仅当它仍在列表中），
          // 否则回退到列表第一个（updated_at 最新）。仅"读"某对话不会 bump updated_at，
          // 所以单靠列表排序会丢掉上次停留的对话，故用 localStorage 精确记住。
          const savedId = parseInt(localStorage.getItem('scriptMasterLastConvId'), 10);
          const target = (Number.isFinite(savedId) && convList.some(cv => cv.id === savedId))
            ? savedId : convList[0].id;
          openConversation(target);
        }
      } catch (e) {
        console.error('[ScriptMaster] 初始化失败:', e);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Socket.IO：监听流式事件 ──
  useEffect(() => {
    const token = api.getToken ? api.getToken() : localStorage.getItem('cineart_token');
    const socket = io(C.API_BASE_URL, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      auth: { token },
    });
    socketRef.current = socket;

    socket.on('model-master-token', ({ conversationId, messageId, delta }) => {
      if (conversationId !== activeIdRef.current) return;
      setMessages(prev => {
        const next = [...prev];
        // 找到当前流式中的占位 assistant 气泡（最后一条且 role=assistant）
        const idx = next.length - 1;
        if (idx >= 0 && next[idx].role === 'assistant') {
          next[idx] = { ...next[idx], id: messageId, content: next[idx].content + delta };
        }
        return next;
      });
      streamingMsgIdRef.current = messageId;
      scrollToBottom();
    });

    socket.on('model-master-done', ({ conversationId, messageId, content, outputTokens, cost, renderUrl }) => {
      if (conversationId !== activeIdRef.current) return;
      setMessages(prev => {
        const next = [...prev];
        const idx = next.length - 1;
        if (idx >= 0 && next[idx].role === 'assistant') {
          next[idx] = { ...next[idx], id: messageId, content, outputTokens, cost, render_url: renderUrl };
        }
        return next;
      });
      setStreaming(false);
      streamingMsgIdRef.current = null;
      setRenderingStatus(null);
      refreshConversationList();
      scrollToBottom();
    });

    socket.on('model-master-error', ({ conversationId, error }) => {
      if (conversationId !== activeIdRef.current) return;
      setMessages(prev => {
        const next = [...prev];
        const idx = next.length - 1;
        if (idx >= 0 && next[idx].role === 'assistant') {
          next[idx] = { ...next[idx], content: `⚠️ ${error || '建模大师生成失败'}`, error: true };
        }
        return next;
      });
      setStreaming(false);
      streamingMsgIdRef.current = null;
      setRenderingStatus(null);
    });

    // 监听渲染状态
    socket.on('model-master-rendering', ({ conversationId, message }) => {
      if (conversationId !== activeIdRef.current) return;
      setRenderingStatus(message);
    });

    return () => { socket.disconnect(); socketRef.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refreshConversationList = useCallback(async () => {
    try {
      const c = await api.get('/api/ai/model-master/conversations');
      setConversations(c?.conversations || []);
    } catch (e) { /* 静默 */ }
  }, []);

  // ── 打开某个对话 ──
  const openConversation = useCallback(async (id) => {
    setActiveId(id);
    setMessages([]);
    autoScrollRef.current = true;  // 打开对话时恢复自动跟随，确保滚到底部
    try {
      const data = await api.get(`/api/ai/model-master/conversations/${id}`);
      setMessages((data?.messages || []).map(m => ({
        id: m.id,
        role: m.role,
        content: m.content,
        images: m.images,
        render_url: m.render_url
      })));
      // 仅当对话绑定的模型在当前可用列表中时才套用，否则保留当前选择（避免陈旧绑定指向已删除模型）
      const savedMid = data?.conversation?.model_id;
      if (savedMid && models.some(m => m.id === savedMid)) setModelId(savedMid);
      // 🔄 关窗续播：若该对话仍有进行中的流式任务，恢复 streaming 态并指向那条 assistant 消息，
      // 后续增量会继续追加到它上面，最终 done 事件用完整文本兜底覆盖
      if (data?.streaming) {
        streamingMsgIdRef.current = data.streaming.messageId;
        setStreaming(true);
      } else {
        setStreaming(false);
        streamingMsgIdRef.current = null;
      }
      scrollToBottom();
    } catch (e) {
      console.error('[ScriptMaster] 打开对话失败:', e);
    }
  }, [scrollToBottom, models]);

  // ── 新建对话 ──
  const handleNewConversation = useCallback(async () => {
    try {
      const data = await api.post('/api/ai/model-master/conversations', { modelId });
      const conv = data?.conversation;
      if (conv) {
        setConversations(prev => [conv, ...prev]);
        setActiveId(conv.id);
        setMessages([]);
      }
    } catch (e) {
      console.error('[ScriptMaster] 新建对话失败:', e);
    }
  }, [modelId]);

  // ── 删除对话（画布内确认弹窗）──
  const handleDelete = useCallback((id, e) => {
    e.stopPropagation();
    setDeleteModal({ id });
  }, []);

  const confirmDelete = useCallback(async () => {
    const id = deleteModal?.id;
    if (!id) return;
    try {
      await api.delete(`/api/ai/model-master/conversations/${id}`);
      setConversations(prev => prev.filter(c => c.id !== id));
      if (activeIdRef.current === id) {
        setActiveId(null); setMessages([]);
        localStorage.removeItem('scriptMasterLastConvId');  // 记忆的对话已删，清掉避免残留
      }
    } catch (e2) {
      console.error('[ScriptMaster] 删除对话失败:', e2);
    } finally {
      setDeleteModal(null);
    }
  }, [deleteModal]);

  // ── 重命名对话（画布内输入弹窗）──
  const handleRename = useCallback((id, e) => {
    e.stopPropagation();
    const cur = conversations.find(c => c.id === id);
    setRenameModal({ id, value: cur?.title || '' });
  }, [conversations]);

  const confirmRename = useCallback(async () => {
    const id = renameModal?.id;
    const title = (renameModal?.value || '').trim();
    if (!id || !title) { setRenameModal(null); return; }
    try {
      await api.patch(`/api/ai/model-master/conversations/${id}`, { title });
      setConversations(prev => prev.map(c => c.id === id ? { ...c, title } : c));
    } catch (e2) {
      console.error('[ScriptMaster] 重命名失败:', e2);
    } finally {
      setRenameModal(null);
    }
  }, [renameModal]);

  // ── 发送 ──
  // sendMessage(text, images)：发送核心，供输入框发送与选项按钮点击复用。
  const sendMessage = useCallback(async (rawText, images = []) => {
    const text = (rawText || '').trim();
    if (!text || streaming) return;

    let convId = activeIdRef.current;
    // 没有激活对话则先建一个
    if (!convId) {
      try {
        const data = await api.post('/api/ai/model-master/conversations', { modelId });
        const conv = data?.conversation;
        if (!conv) return;
        convId = conv.id;
        setConversations(prev => [conv, ...prev]);
        setActiveId(conv.id);
        activeIdRef.current = conv.id;
      } catch (e) {
        console.error('[ScriptMaster] 新建对话失败:', e);
        return;
      }
    }

    // 乐观追加用户消息（含图片）+ 空 assistant 占位（流式追加目标）
    setMessages(prev => [
      ...prev,
      { id: `local-u-${Date.now()}`, role: 'user', content: text, images: images.length ? JSON.stringify(images) : null },
      { id: `local-a-${Date.now()}`, role: 'assistant', content: '' },
    ]);
    setStreaming(true);
    autoScrollRef.current = true;  // 新发一条消息，恢复自动跟随到底部
    scrollToBottom();

    try {
      const resp = await api.post('/api/ai/model-master/chat', {
        conversationId: convId,
        content: text,
        modelId,
        images: images.length ? images : undefined,
      });
      if (resp && resp.success === false) {
        throw new Error(resp.error || '请求失败');
      }
    } catch (e) {
      setMessages(prev => {
        const next = [...prev];
        const idx = next.length - 1;
        if (idx >= 0 && next[idx].role === 'assistant') {
          next[idx] = { ...next[idx], content: `⚠️ ${e.message || '请求失败'}`, error: true };
        }
        return next;
      });
      setStreaming(false);
    }
  }, [streaming, modelId, scrollToBottom]);

  // 输入框发送：取 input + 图片，清空后交给 sendMessage
  const handleSend = useCallback(() => {
    const text = input.trim();
    if (!text || streaming) return;
    const imagesToSend = [...uploadedImages];
    setInput('');
    setUploadedImages([]);  // 清空上传的图片
    sendMessage(text, imagesToSend);
  }, [input, streaming, uploadedImages, sendMessage]);

  // 选项单选：记录某条消息某个选项块选中的选项（不立即发送）
  const handleChoiceSelect = useCallback((messageId, blockIdx, option) => {
    setChoiceDraft(prev => ({
      ...prev,
      [messageId]: { ...(prev[messageId] || {}), [blockIdx]: option },
    }));
  }, []);

  // 提交一轮问询：把该消息所有选项块的选择拼成一条消息一次性发送。
  // 带标签的拼成「标签：选项」，无标签的只放选项文字；多组用换行分隔。
  const handleChoiceSubmit = useCallback((messageId, choiceBlocks) => {
    if (streaming) return;
    const picked = choiceDraft[messageId] || {};
    const lines = choiceBlocks.map(b => {
      const opt = picked[b.blockIdx];
      if (!opt) return null;
      return b.label ? `${b.label}：${opt}` : opt;
    }).filter(Boolean);
    if (lines.length !== choiceBlocks.length) return;  // 未选齐，不提交
    sendMessage(lines.join('\n'));
  }, [streaming, choiceDraft, sendMessage]);

  // 图片上传处理
  const handleImageUpload = useCallback(async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    // 限制最多 5 张
    if (uploadedImages.length + files.length > 5) {
      alert('最多上传 5 张参考图');
      return;
    }

    // 转 base64
    const base64Images = await Promise.all(
      files.map(file => {
        return new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = () => resolve(null);
          reader.readAsDataURL(file);
        });
      })
    );

    const validImages = base64Images.filter(Boolean);
    setUploadedImages(prev => [...prev, ...validImages]);

    // 清空 input，允许重复选择同一文件
    if (e.target) e.target.value = '';
  }, [uploadedImages]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // ── 选中文本右键菜单：屏蔽原生菜单，自定义「复制 / 粘贴到新节点」──
  const rootRef = useRef(null);
  const handleContextMenu = useCallback((e) => {
    e.preventDefault();  // 屏蔽浏览器原生右键菜单
    const sel = window.getSelection();
    const text = sel ? sel.toString() : '';
    if (!text.trim()) { setCtxMenu(null); return; }  // 没选中内容则不弹
    setCtxMenu({ x: e.clientX, y: e.clientY, text });
  }, []);

  const copyText = useCallback(async (text) => {
    // 回退实现（textarea + execCommand）已抽到 utils/browserCompat，这里只管调用
    const ok = await copyTextToClipboard(text);
    if (!ok) console.warn('[ScriptMaster] 复制失败');
  }, []);

  const handlePasteToNode = useCallback((text) => {
    onSpawnTextNode?.(text);
    setCtxMenu(null);
  }, [onSpawnTextNode]);

  // 点击别处 / 滚动 / Esc 关闭右键菜单
  useEffect(() => {
    if (!ctxMenu) return;
    const close = () => setCtxMenu(null);
    const onKey = (e) => { if (e.key === 'Escape') setCtxMenu(null); };
    window.addEventListener('mousedown', close);
    window.addEventListener('wheel', close, { passive: true });
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', close);
      window.removeEventListener('wheel', close);
      window.removeEventListener('keydown', onKey);
    };
  }, [ctxMenu]);

  // ── 拖动输入区上沿分割线，调整输入区高度（仿模型节点控制台）──
  // 向上拖变高、向下拖变矮；松手持久化。
  const handleInputResizeStart = useCallback((e) => {
    e.preventDefault();
    const startY = e.clientY;
    const startH = inputHeight;
    let latest = startH;
    const onMove = (ev) => {
      const delta = startY - ev.clientY;  // 向上拖 → delta 为正 → 变高
      // 下限 140：分割线(8) + 底栏(约56) + 文本框至少留一行多，保证不被压扁
      latest = Math.max(140, Math.min(window.innerHeight - 160, startH + delta));
      setInputHeight(latest);
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      localStorage.setItem('scriptMasterInputHeight', String(Math.round(latest)));
    };
    document.body.style.cursor = 'row-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [inputHeight]);

  // ── 样式 ──
  const panelBg = '#0f172a';
  const borderColor = 'rgba(255,255,255,0.08)';

  return (
    <div ref={rootRef} onContextMenu={handleContextMenu} style={{ position: 'relative', display: 'flex', width: '100%', height: '100%', background: panelBg, color: '#e2e8f0', overflow: 'hidden', fontSize: 14 }}>
      {/* ── 主区：消息列表 + 输入 ── */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {/* 顶栏 */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderBottom: `1px solid ${borderColor}` }}>
          <button onClick={() => setHistoryOpen(v => !v)} title="对话目录"
            style={{
              flexShrink: 0, width: 28, height: 28, borderRadius: '50%',
              display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
              background: historyOpen ? '#3b82f6' : 'rgba(255,255,255,0.08)',
              border: `1px solid ${historyOpen ? '#3b82f6' : borderColor}`,
              color: historyOpen ? '#fff' : '#94a3b8', transition: 'all 0.18s',
            }}>
            <Plus size={16} style={{ transform: historyOpen ? 'rotate(45deg)' : 'none', transition: 'transform 0.2s' }} />
          </button>
          <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 700, fontSize: 15, color: '#fbbf24', minWidth: 0 }}>
            <ScrollText size={18} style={{ flexShrink: 0 }} /> 建模大师
            {activeTitle && (
              <span style={{ color: '#e2e8f0', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                《{activeTitle}》
              </span>
            )}
          </span>
          <div style={{ flex: 1 }} />
          {onClose && (
            <button onClick={onClose} title="折叠" style={{ background: 'transparent', border: 'none', color: '#64748b', cursor: 'pointer', padding: 4, display: 'flex' }}>
              {layout === 'bottom' ? <PanelBottomClose size={18} /> : <PanelLeftClose size={18} />}
            </button>
          )}
        </div>

        {/* 消息列表 —— 滚动条置于左侧（direction:rtl 容器 + 内容回正 ltr）；
            消息满铺等宽，不做左右错位 */}
        <div ref={scrollRef} onScroll={handleScroll} style={{ flex: 1, overflowY: 'auto', direction: 'rtl', padding: '16px 14px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          {messages.length === 0 && (
            <div style={{ direction: 'ltr', color: '#475569', textAlign: 'center', marginTop: 40, fontSize: 13, lineHeight: 1.8 }}>
              开始和建模大师对话吧。<br />描述你的 3D 场景需求，它会为你生成 Blender 脚本并渲染。
            </div>
          )}
          {messages.map((m, i) => {
            const isLast = i === messages.length - 1;
            // 流式中的预估消耗（仅当前正在生成的 assistant 气泡显示）
            const showEstimate = m.role === 'assistant' && streaming && isLast && pricePer1kOutput > 0 && m.content;
            const estCost = showEstimate ? (estimateTokens(m.content) / 1000 * pricePer1kOutput) : 0;
            // 选项是否可操作：仅「最后一条 assistant 且非流式中」可选/可提交，历史旧选项置灰防误触
            const choicesActive = m.role === 'assistant' && !m.error && isLast && !streaming;
            // 解析正文段落；抽出所有选项块，判断本轮是否已全选齐
            const segs = (m.role === 'assistant' && m.content) ? parseChoiceSegments(m.content) : null;
            const choiceBlocks = segs ? segs.filter(s => s.type === 'choices') : [];
            const picked = choiceDraft[m.id] || {};
            const allPicked = choiceBlocks.length > 0 && choiceBlocks.every(b => picked[b.blockIdx]);
            return (
            <div key={m.id || i} style={{ direction: 'ltr', display: 'flex', flexDirection: 'column', alignItems: m.role === 'user' ? 'flex-end' : 'stretch' }}>
              <div style={{
                width: m.role === 'user' ? 'auto' : '100%', maxWidth: m.role === 'user' ? 300 : '100%',
                padding: '10px 14px', borderRadius: 12, wordBreak: 'break-word', lineHeight: 1.6, boxSizing: 'border-box',
                whiteSpace: m.role === 'user' ? 'pre-wrap' : 'normal',
                background: m.role === 'user' ? 'rgba(59,130,246,0.18)' : (m.error ? 'rgba(239,68,68,0.15)' : 'rgba(255,255,255,0.06)'),
                color: m.role === 'user' ? '#e2e8f0' : (m.error ? '#fca5a5' : '#e2e8f0'),
                border: m.role === 'user' ? '1px solid rgba(59,130,246,0.35)' : `1px solid ${borderColor}`,
              }}>
                {/* user：纯文本 + 参考图；assistant：markdown 渲染 + 选项块抽成单选按钮 + 渲染结果 */}
                {m.role === 'user'
                  ? (
                    <>
                      {m.content || ''}
                      {/* 用户上传的参考图 */}
                      {m.images && (() => {
                        try {
                          const imgs = JSON.parse(m.images);
                          return Array.isArray(imgs) && imgs.length > 0 ? (
                            <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                              {imgs.map((img, idx) => (
                                <img
                                  key={idx}
                                  src={img}
                                  alt="参考图"
                                  style={{ maxWidth: 200, maxHeight: 200, borderRadius: 6, border: '1px solid rgba(59,130,246,0.35)' }}
                                />
                              ))}
                            </div>
                          ) : null;
                        } catch {
                          return null;
                        }
                      })()}
                    </>
                  )
                  : (m.content
                      ? segs.map((seg, si) => (
                          seg.type === 'text'
                            ? splitOutlineSegments(seg.value).map((os, oi) => {
                                const grid = os.shotCount != null ? shotCountToGrid(os.shotCount) : null;
                                const canImport = grid != null && !!onImportToStoryboard;
                                const hkey = `${m.id || i}-${si}-${oi}`;
                                return (
                                  <div key={`${si}-${oi}`}
                                    onMouseEnter={() => canImport && setHoverImportKey(hkey)}
                                    onMouseLeave={() => canImport && setHoverImportKey(k => (k === hkey ? null : k))}>
                                    <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>{preprocessMentions(os.text)}</ReactMarkdown>
                                    {canImport && (
                                      <div style={{ display: 'flex', justifyContent: 'flex-end', margin: '2px 0 6px' }}>
                                        <button
                                          onClick={() => onImportToStoryboard(os.text, grid)}
                                          title="在画布上新建分镜大师节点，并把这段大纲灌进去"
                                          style={{
                                            display: 'inline-flex', alignItems: 'center', gap: 6,
                                            padding: '6px 12px', borderRadius: 8,
                                            fontSize: 12.5, fontWeight: 600, cursor: 'pointer',
                                            background: 'rgba(59,130,246,0.14)',
                                            border: '1px solid rgba(59,130,246,0.4)',
                                            color: '#bfdbfe',
                                            opacity: hoverImportKey === hkey ? 1 : 0.4,
                                            transition: 'opacity 0.15s, background 0.15s',
                                          }}>
                                          <Clapperboard size={14} />
                                          导入到分镜大师（{os.shotCount}镜 → {grid}宫格）
                                        </button>
                                      </div>
                                    )}
                                    {os.trailingRule && (
                                      <hr style={{ border: 'none', borderTop: '1px solid rgba(255,255,255,0.1)', margin: '12px 0' }} />
                                    )}
                                  </div>
                                );
                              })
                            : (
                              <div key={si} style={{ display: 'flex', flexDirection: 'column', gap: 8, margin: '6px 0 10px' }}>
                                {seg.label && (
                                  <div style={{ fontSize: 13, fontWeight: 700, color: '#fbbf24' }}>{seg.label}</div>
                                )}
                                {seg.options.map((opt, oi) => {
                                  const selected = picked[seg.blockIdx] === opt;
                                  return (
                                  <button key={oi} onClick={() => choicesActive && handleChoiceSelect(m.id, seg.blockIdx, opt)} disabled={!choicesActive}
                                    style={{
                                      textAlign: 'left', padding: '10px 14px', borderRadius: 10, fontSize: 13.5, lineHeight: 1.5,
                                      cursor: choicesActive ? 'pointer' : 'default',
                                      background: selected ? 'rgba(59,130,246,0.3)' : (choicesActive ? 'rgba(59,130,246,0.1)' : 'rgba(255,255,255,0.04)'),
                                      border: `1px solid ${selected ? '#3b82f6' : (choicesActive ? 'rgba(59,130,246,0.35)' : 'rgba(255,255,255,0.08)')}`,
                                      color: selected ? '#fff' : (choicesActive ? '#bfdbfe' : '#64748b'),
                                      fontWeight: selected ? 600 : 400, transition: 'all 0.15s',
                                      display: 'flex', alignItems: 'center', gap: 8,
                                    }}
                                    onMouseEnter={e => { if (choicesActive && !selected) { e.currentTarget.style.background = 'rgba(59,130,246,0.2)'; e.currentTarget.style.borderColor = '#3b82f6'; } }}
                                    onMouseLeave={e => { if (choicesActive && !selected) { e.currentTarget.style.background = 'rgba(59,130,246,0.1)'; e.currentTarget.style.borderColor = 'rgba(59,130,246,0.35)'; } }}>
                                    {/* 单选圆点 */}
                                    <span style={{ flexShrink: 0, width: 14, height: 14, borderRadius: '50%', border: `2px solid ${selected ? '#fff' : '#64748b'}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                      {selected && <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#fff' }} />}
                                    </span>
                                    <span>{opt}</span>
                                  </button>
                                  );
                                })}
                              </div>
                            )
                        ))
                      : (m.role === 'assistant' && streaming ? <Loader2 size={16} className="sm-spin" /> : ''))}
                {/* 复制按钮：仅 assistant 消息显示，右下角小按钮 */}
                {m.role === 'assistant' && !m.error && m.content && (
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 4 }}>
                    <button
                      onClick={() => copyText(m.content)}
                      title="复制这条消息"
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 4,
                        padding: '4px 8px', borderRadius: 6,
                        fontSize: 11, fontWeight: 500, cursor: 'pointer',
                        background: 'rgba(255,255,255,0.06)',
                        border: '1px solid rgba(255,255,255,0.12)',
                        color: '#94a3b8',
                        transition: 'all 0.15s',
                      }}
                      onMouseEnter={e => {
                        e.currentTarget.style.background = 'rgba(59,130,246,0.15)';
                        e.currentTarget.style.borderColor = 'rgba(59,130,246,0.4)';
                        e.currentTarget.style.color = '#bfdbfe';
                      }}
                      onMouseLeave={e => {
                        e.currentTarget.style.background = 'rgba(255,255,255,0.06)';
                        e.currentTarget.style.borderColor = 'rgba(255,255,255,0.12)';
                        e.currentTarget.style.color = '#94a3b8';
                      }}>
                      复制
                    </button>
                  </div>
                )}
                {/* 一轮问询的提交按钮：本条消息有选项块时显示，全选齐才可点 */}
                {choiceBlocks.length > 0 && (
                  <button onClick={() => allPicked && choicesActive && handleChoiceSubmit(m.id, choiceBlocks)}
                    disabled={!allPicked || !choicesActive}
                    style={{
                      marginTop: 4, width: '100%', padding: '10px 0', borderRadius: 10, fontSize: 14, fontWeight: 700, border: 'none',
                      cursor: (allPicked && choicesActive) ? 'pointer' : 'not-allowed',
                      background: (allPicked && choicesActive) ? '#3b82f6' : 'rgba(255,255,255,0.06)',
                      color: (allPicked && choicesActive) ? '#fff' : '#64748b',
                      transition: 'all 0.15s',
                    }}>
                    {choicesActive
                      ? (allPicked ? '提交选择' : `请完成选择（${Object.keys(picked).length}/${choiceBlocks.length}）`)
                      : '已提交'}
                  </button>
                )}
              </div>
              {/* 助手返回的渲染结果 */}
              {m.role === 'assistant' && m.render_url && (
                <div style={{ marginTop: 12, textAlign: 'center' }}>
                  <img
                    src={m.render_url}
                    alt="渲染结果"
                    style={{ maxWidth: '100%', borderRadius: 8, border: '1px solid rgba(255,255,255,0.12)' }}
                  />
                </div>
              )}
              {/* 费用：流式中显示实时预估，完成后显示真实结算 */}
              {showEstimate && (
                <span style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>预估消耗 ≈ {Math.round(estCost)} 积分…</span>
              )}
              {m.role === 'assistant' && !m.error && typeof m.cost === 'number' && m.cost > 0 && (
                <span style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>本条消耗 {Math.round(m.cost)} 积分（{m.outputTokens} 输出 tokens）</span>
              )}
            </div>
            );
          })}
          {/* 渲染中状态提示 */}
          {renderingStatus && (
            <div style={{ direction: 'ltr', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '12px 14px', borderRadius: 10, background: 'rgba(59,130,246,0.12)', border: '1px solid rgba(59,130,246,0.3)', color: '#bfdbfe', fontSize: 13 }}>
              <Loader2 size={16} className="sm-spin" />
              <span>{renderingStatus}</span>
            </div>
          )}
        </div>

        {/* ── 输入区（仿模型节点控制台风格）──
            顶部可拖动分割线调高度；中间 prompt 文本框；
            底栏：左下角模型选择（可换模型/未来多模型），右下角积分提示 + 发送。
            最大宽度固定 720，居中，两侧留空——面板再宽控制台也不变大 */}
        <div style={{ flexShrink: 0, height: inputHeight, width: '100%', maxWidth: 720, alignSelf: 'center', display: 'flex', flexDirection: 'column', background: '#222222', border: '0.5px solid #444', borderRadius: 15, margin: '8px auto', overflow: 'hidden' }}>
          {/* 可拖动分割线 */}
          <div
            onMouseDown={handleInputResizeStart}
            title="拖动调整输入区高度"
            style={{ height: 8, flexShrink: 0, cursor: 'row-resize', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            onMouseEnter={e => { e.currentTarget.firstChild.style.background = '#3b82f6'; }}
            onMouseLeave={e => { e.currentTarget.firstChild.style.background = 'rgba(255,255,255,0.18)'; }}
          >
            <div style={{ width: 40, height: 3, borderRadius: 2, background: 'rgba(255,255,255,0.18)', transition: 'background 0.15s' }} />
          </div>

          {/* prompt 文本框 */}
          <div style={{ flex: 1, minHeight: 0, padding: '0 14px', display: 'flex', flexDirection: 'column', gap: 8 }}>
            {/* 图片预览区 */}
            {uploadedImages.length > 0 && (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', paddingTop: 8 }}>
                {uploadedImages.map((img, idx) => (
                  <div key={idx} style={{ position: 'relative', width: 80, height: 80 }}>
                    <img
                      src={img}
                      alt="参考图"
                      style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 6, border: '1px solid #333' }}
                    />
                    <button
                      onClick={() => setUploadedImages(prev => prev.filter((_, i) => i !== idx))}
                      style={{
                        position: 'absolute', top: 2, right: 2,
                        width: 20, height: 20, borderRadius: '50%',
                        background: 'rgba(0,0,0,0.7)', color: '#fff',
                        border: 'none', cursor: 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        padding: 0,
                      }}
                    >
                      <X size={12} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <textarea
              ref={textareaRef}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              data-voice-shortcut="true"
              placeholder="描述你想建立的 3D 场景，Enter 发送，Shift+Enter 换行…"
              className="custom-scrollbar"
              style={{
                flex: 1, width: '100%', resize: 'none', background: 'rgba(0,0,0,0.2)',
                border: '1px solid #333', borderRadius: 12, color: '#ffffff',
                fontSize: 14, lineHeight: 1.6, outline: 'none', padding: 12, boxSizing: 'border-box',
                fontFamily: 'inherit',
              }}
            />
          </div>

          {/* 底栏：图片上传 + 模型选择（左） + 积分 & 发送（右） */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '0.5px solid #333', padding: '10px 14px', gap: 10 }}>
            {/* 左下角：图片上传 + 模型选择 */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {/* 图片上传按钮 */}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                style={{ display: 'none' }}
                onChange={handleImageUpload}
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadedImages.length >= 5}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 10px',
                  borderRadius: 8, cursor: uploadedImages.length >= 5 ? 'not-allowed' : 'pointer',
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid #333', color: '#94a3b8',
                  fontSize: 13,
                  opacity: uploadedImages.length >= 5 ? 0.5 : 1,
                }}
                title={uploadedImages.length >= 5 ? '最多上传 5 张参考图' : '上传参考图'}
              >
                <ImageIcon size={16} />
                <span>{uploadedImages.length > 0 ? `${uploadedImages.length}/5` : '参考图'}</span>
              </button>

              {/* 模型选择（点击展开下拉，支持未来多模型） */}
              <div style={{ position: 'relative' }}>
              <button
                ref={modelBtnRef}
                onClick={() => {
                  setModelPickerOpen(v => {
                    const next = !v;
                    if (next && modelBtnRef.current) {
                      const r = modelBtnRef.current.getBoundingClientRect();
                      setModelMenuRect({ left: r.left, bottom: window.innerHeight - r.top + 6 });
                    }
                    return next;
                  });
                }}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 10px',
                  borderRadius: 8, cursor: 'pointer', maxWidth: 220,
                  background: modelPickerOpen ? 'rgba(255,255,255,0.12)' : 'rgba(255,255,255,0.05)',
                  border: `1px solid ${borderColor}`, color: '#e2e8f0',
                }}>
                {currentModel?.logoUrl ? (
                  <img src={currentModel.logoUrl} alt="" style={{ width: 18, height: 18, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
                ) : (
                  <div style={{ width: 18, height: 18, borderRadius: '50%', background: 'rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Bot size={12} color="#94a3b8" />
                  </div>
                )}
                <span style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {currentModel ? cleanModelName(currentModel.name) : '选择模型'}
                </span>
                <ChevronDown size={13} style={{ color: '#64748b', transform: modelPickerOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s', flexShrink: 0 }} />
              </button>

              {/* 上拉菜单：用 portal 固定定位渲染到 body，避开输入控制台 overflow:hidden 的裁切，
                  否则模型多时菜单顶部（含置顶/选中项）会被切掉 */}
              {modelPickerOpen && modelMenuRect && createPortal(
                <>
                  <div onClick={() => setModelPickerOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 10060 }} />
                  <div style={{
                    position: 'fixed', left: modelMenuRect.left, bottom: modelMenuRect.bottom, zIndex: 10061,
                    minWidth: 200, maxHeight: 300, overflowY: 'auto',
                    background: '#1e293b', border: `1px solid ${borderColor}`, borderRadius: 10, padding: 6,
                    boxShadow: '0 10px 30px rgba(0,0,0,0.5)',
                  }} className="custom-scrollbar">
                    {models.length === 0 && (
                      <div style={{ color: '#475569', fontSize: 12, textAlign: 'center', padding: '10px 0' }}>暂无可用模型</div>
                    )}
                    {models.map(m => (
                      <div key={m.id} onClick={() => { setModelId(m.id); setModelPickerOpen(false); }}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderRadius: 8, cursor: 'pointer',
                          background: m.id === modelId ? 'rgba(59,130,246,0.18)' : 'transparent',
                        }}
                        onMouseEnter={e => { if (m.id !== modelId) e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; }}
                        onMouseLeave={e => { if (m.id !== modelId) e.currentTarget.style.background = 'transparent'; }}>
                        {m.logoUrl ? (
                          <img src={m.logoUrl} alt="" style={{ width: 20, height: 20, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
                        ) : (
                          <div style={{ width: 20, height: 20, borderRadius: '50%', background: 'rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <Bot size={13} color="#94a3b8" />
                          </div>
                        )}
                        <span style={{ fontSize: 13, color: '#e2e8f0', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cleanModelName(m.name)}</span>
                        {(m.pricePer1kOutput > 0) && <span style={{ fontSize: 10, color: '#fbbf24', flexShrink: 0 }}>{m.pricePer1kOutput}/千token</span>}
                      </div>
                    ))}
                  </div>
                </>,
                document.body
              )}
            </div>
            </div>

            {/* 右下角：积分单价提示 + 语音 + 发送按钮 */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {pricePer1kOutput > 0 && (
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: 3, padding: '4px 10px', borderRadius: 8,
                  background: 'rgba(251,191,36,0.1)', color: '#fbbf24', fontSize: 12, fontWeight: 600,
                }}>
                  <Sparkle size={12} fill="#fbbf24" strokeWidth={0} /> {pricePer1kOutput}/千token
                </span>
              )}
              <VoiceMicButton
                onResult={(newText) => setInput(newText)}
                currentText={input}
                textareaRef={textareaRef}
                size={22}
                position="inline"
              />
              <button onClick={handleSend} disabled={streaming || !input.trim()}
                style={{ flexShrink: 0, width: 36, height: 36, borderRadius: '50%', border: 'none', cursor: streaming || !input.trim() ? 'not-allowed' : 'pointer',
                  background: streaming || !input.trim() ? '#6b7280' : '#3b82f6', opacity: streaming || !input.trim() ? 0.6 : 1,
                  color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow: streaming || !input.trim() ? 'none' : '0 4px 12px rgba(59,130,246,0.3)' }}>
                {streaming ? <Loader2 size={18} className="sm-spin" /> : <Send size={18} />}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ── 对话目录悬浮窗 ──
          点击顶栏圆形「+」展开；半透明浮于建模大师内容之上；
          鼠标移出该区域（无论是否选择对话）自动收起 */}
      {historyOpen && (
        <div
          onMouseLeave={() => setHistoryOpen(false)}
          style={{
            position: 'absolute', top: 48, left: 10, zIndex: 20,
            // 底沿不越过下方控制台上沿：控制台占 inputHeight + 16(上下各 8 外边距)，再留 8 间隙
            width: 220, bottom: inputHeight + 24,
            display: 'flex', flexDirection: 'column',
            background: 'rgba(11,18,32,0.82)', backdropFilter: 'blur(10px)',
            border: `1px solid ${borderColor}`, borderRadius: 12,
            boxShadow: '0 10px 30px rgba(0,0,0,0.5)', overflow: 'hidden',
          }}
          className="sm-history-pop">
          <div style={{ padding: 10 }}>
            <button onClick={handleNewConversation}
              style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '8px 0', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
              <Plus size={16} /> 新建对话
            </button>
          </div>
          <div style={{ flex: 1, overflowY: 'auto', padding: '0 8px 8px' }}>
            {conversations.length === 0 && (
              <div style={{ color: '#475569', fontSize: 12, textAlign: 'center', marginTop: 20 }}>暂无对话</div>
            )}
            {conversations.map(c => (
              <div key={c.id} onClick={() => openConversation(c.id)}
                style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 10px', borderRadius: 8, cursor: 'pointer', marginBottom: 4,
                  background: c.id === activeId ? 'rgba(59,130,246,0.22)' : 'transparent' }}
                className="sm-conv-item">
                <MessageSquare size={14} style={{ flexShrink: 0, color: '#64748b' }} />
                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 13 }}>{c.title || '新对话'}</span>
                <Edit2 size={13} onClick={(e) => handleRename(c.id, e)} style={{ flexShrink: 0, color: '#475569', cursor: 'pointer' }} className="sm-conv-action" />
                <Trash2 size={13} onClick={(e) => handleDelete(c.id, e)} style={{ flexShrink: 0, color: '#475569', cursor: 'pointer' }} className="sm-conv-action" />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── 选中文本右键菜单：复制 / 粘贴到新节点 ── */}
      {ctxMenu && (
        <div
          style={{ position: 'fixed', top: ctxMenu.y, left: ctxMenu.x, zIndex: 10050,
            minWidth: 150, padding: 4, background: '#1e293b', border: `1px solid ${borderColor}`,
            borderRadius: 8, boxShadow: '0 10px 30px rgba(0,0,0,0.5)' }}
          onMouseDown={e => e.stopPropagation()}
          onContextMenu={e => e.preventDefault()}>
          <div onClick={() => { copyText(ctxMenu.text); setCtxMenu(null); }}
            style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderRadius: 6, cursor: 'pointer', fontSize: 13, color: '#e2e8f0' }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.08)'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
            <Copy size={14} color="#94a3b8" /> 复制
          </div>
          <div onClick={() => handlePasteToNode(ctxMenu.text)}
            style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderRadius: 6, cursor: 'pointer', fontSize: 13, color: '#e2e8f0' }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.08)'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
            <FilePlus size={14} color="#94a3b8" /> 粘贴到新节点
          </div>
        </div>
      )}

      {/* ── 画布内重命名弹窗 ── */}
      {renameModal && (
        <div style={{ position: 'absolute', inset: 0, zIndex: 10040, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(2px)' }}
          onMouseDown={e => { e.stopPropagation(); if (e.target === e.currentTarget) setRenameModal(null); }}>
          <div style={{ width: 300, maxWidth: '86%', background: '#1a1a2e', border: `1px solid ${borderColor}`, borderRadius: 12, padding: 20 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#e2e8f0', marginBottom: 14 }}>重命名对话</div>
            <input autoFocus value={renameModal.value}
              onChange={e => setRenameModal(m => ({ ...m, value: e.target.value }))}
              onKeyDown={e => { if (e.key === 'Enter') confirmRename(); if (e.key === 'Escape') setRenameModal(null); }}
              style={{ width: '100%', padding: '10px 12px', borderRadius: 8, background: '#0a0a0a', border: `1px solid ${borderColor}`, color: '#fff', fontSize: 14, outline: 'none', boxSizing: 'border-box' }} />
            <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
              <button onClick={() => setRenameModal(null)} style={{ padding: '8px 16px', borderRadius: 8, border: `1px solid ${borderColor}`, background: 'transparent', color: '#94a3b8', cursor: 'pointer', fontSize: 13 }}>取消</button>
              <button onClick={confirmRename} disabled={!renameModal.value.trim()}
                style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: renameModal.value.trim() ? '#3b82f6' : '#1e3a5f', color: renameModal.value.trim() ? '#fff' : '#94a3b8', cursor: renameModal.value.trim() ? 'pointer' : 'not-allowed', fontSize: 13, fontWeight: 600 }}>确认</button>
            </div>
          </div>
        </div>
      )}

      {/* ── 画布内删除确认弹窗 ── */}
      {deleteModal && (
        <div style={{ position: 'absolute', inset: 0, zIndex: 10040, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(2px)' }}
          onMouseDown={e => { e.stopPropagation(); if (e.target === e.currentTarget) setDeleteModal(null); }}>
          <div style={{ width: 300, maxWidth: '86%', background: '#1a1a2e', border: `1px solid ${borderColor}`, borderRadius: 12, padding: 20 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#e2e8f0', marginBottom: 10 }}>删除对话</div>
            <div style={{ fontSize: 13, color: '#94a3b8', lineHeight: 1.6, marginBottom: 16 }}>确定删除这个对话？删除后无法恢复。</div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => setDeleteModal(null)} style={{ padding: '8px 16px', borderRadius: 8, border: `1px solid ${borderColor}`, background: 'transparent', color: '#94a3b8', cursor: 'pointer', fontSize: 13 }}>取消</button>
              <button onClick={confirmDelete} style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: '#ef4444', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>删除</button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes sm-spin { to { transform: rotate(360deg); } }
        @keyframes sm-pop-in { from { opacity: 0; transform: translateY(-6px); } to { opacity: 1; transform: translateY(0); } }
        .sm-history-pop { animation: sm-pop-in 0.16s ease-out; }
        .sm-spin { animation: sm-spin 0.8s linear infinite; }
        .sm-conv-item .sm-conv-action { opacity: 0; transition: opacity 0.15s; }
        .sm-conv-item:hover .sm-conv-action { opacity: 1; }
        .sm-conv-item:hover { background: rgba(255,255,255,0.05); }
      `}</style>
    </div>
  );
}
